import ExcelJS from "exceljs";

import {
  SemifinishedRequirementItem,
  columnTotals,
  monthLabel
} from "./pivotSemifinishedRequirement";

/** Reportify header green (ARGB) — как в buildTransportCostsWorkbook. */
export const HEADER_FILL = "FF72BF44";
/** Reportify thin border color (ARGB). */
export const BORDER_COLOR = "FFAAAAAA";
export const FONT_NAME = "Verdana";
export const FONT_SIZE = 8;
export const PRODUCT_COL_WIDTH = 40;
export const SEMIFINISHED_COL_WIDTH = 30;
export const MONTH_COL_WIDTH = 16;

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } }
};

function toExportNumber(value: any): number {
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Трёхуровневая шапка: строка 1 — «Продукт» (± «Полуфабрикат») и месяцы
 * (объединение на 2 колонки), строка 2 — «потребление, кг» / «полуфабрикат, кг»,
 * строка 3 — «Итого:» с суммами колонок (те же, что в шапке отчёта на экране).
 * Стилизуются все ячейки диапазонов, включая slave-ячейки объединений,
 * чтобы границы отрисовывались по краям merged-блоков.
 */
function addHeader(
  worksheet: ExcelJS.Worksheet,
  items: SemifinishedRequirementItem[],
  months: string[],
  splitBySemifinished: boolean
) {
  const firstMonthCol = splitBySemifinished ? 3 : 2;
  const lastCol = firstMonthCol + months.length * 2 - 1;

  worksheet.getCell(1, 1).value = "Продукт";
  if (splitBySemifinished) {
    worksheet.getCell(1, 2).value = "Полуфабрикат";
  }
  worksheet.mergeCells(1, 1, 2, 1);
  if (splitBySemifinished) {
    worksheet.mergeCells(1, 2, 2, 2);
  }

  months.forEach((month: string, idx: number) => {
    const col = firstMonthCol + idx * 2;
    worksheet.getCell(1, col).value = monthLabel(month);
    worksheet.mergeCells(1, col, 1, col + 1);
    worksheet.getCell(2, col).value = "потребление, кг";
    worksheet.getCell(2, col + 1).value = "полуфабрикат, кг";
  });

  const styleHeaderCell = (cell: ExcelJS.Cell) => {
    cell.font = { name: FONT_NAME, size: FONT_SIZE, bold: true };
    cell.border = thinBorder as ExcelJS.Borders;
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL }
    };
  };

  for (let col = 1; col <= lastCol; col++) {
    styleHeaderCell(worksheet.getCell(1, col));
    styleHeaderCell(worksheet.getCell(2, col));
  }

  // строка сумм в шапке — как в отчёте на экране
  const totals = columnTotals(items, months);
  worksheet.mergeCells(3, 1, 3, firstMonthCol - 1);
  for (let col = 1; col <= lastCol; col++) {
    styleHeaderCell(worksheet.getCell(3, col));
  }
  worksheet.getCell(3, 1).value = "Итого:";
  worksheet.getCell(3, 1).alignment = {
    vertical: "middle",
    horizontal: "left"
  };
  months.forEach((month: string, idx: number) => {
    const col = firstMonthCol + idx * 2;
    const monthTotals = totals[month] || {
      consumptionKg: 0,
      requirementKg: 0
    };
    const consumptionCell = worksheet.getCell(3, col);
    consumptionCell.value = monthTotals.consumptionKg;
    consumptionCell.numFmt = "0.000";
    consumptionCell.alignment = {
      vertical: "middle",
      horizontal: "right"
    };
    const requirementCell = worksheet.getCell(3, col + 1);
    requirementCell.value = monthTotals.requirementKg;
    requirementCell.numFmt = "0.000";
    requirementCell.alignment = {
      vertical: "middle",
      horizontal: "right"
    };
  });

  worksheet.getRow(1).height = 24;
  worksheet.getRow(2).height = 20;
  worksheet.getRow(3).height = 18;
}

/**
 * Матрица «продукт (× полуфабрикат) × месяцы» → стилизованная книга XLSX:
 * зелёная шапка со строкой «Итого» (суммы колонок, как на экране),
 * тонкие границы, без заливки колонок данных.
 */
export function buildSemifinishedRequirementWorkbook(
  items: SemifinishedRequirementItem[],
  months: string[],
  splitBySemifinished: boolean,
  sheetName = "Отчет"
): ExcelJS.Workbook {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  const monthList = months || [];

  addHeader(worksheet, items || [], monthList, splitBySemifinished);
  const firstMonthCol = splitBySemifinished ? 3 : 2;

  (items || []).forEach(item => {
    const row: any[] = [item.productName];
    if (splitBySemifinished) {
      row.push(item.semifinishedName || "");
    }
    monthList.forEach(month => {
      const cell = item.months[month] || {
        consumptionKg: 0,
        requirementKg: 0
      };
      row.push(toExportNumber(cell.consumptionKg));
      row.push(toExportNumber(cell.requirementKg));
    });
    worksheet.addRow(row);
  });

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber <= 3) {
      return;
    }
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { name: FONT_NAME, size: FONT_SIZE };
      cell.border = thinBorder as ExcelJS.Borders;
      if (colNumber >= firstMonthCol) {
        cell.value = toExportNumber(cell.value);
        cell.numFmt = "0.000";
        cell.alignment = { vertical: "middle", horizontal: "right" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      }
    });
  });

  worksheet.getColumn(1).width = PRODUCT_COL_WIDTH;
  if (splitBySemifinished) {
    worksheet.getColumn(2).width = SEMIFINISHED_COL_WIDTH;
  }
  const lastCol = firstMonthCol + monthList.length * 2 - 1;
  for (let i = firstMonthCol; i <= lastCol; i++) {
    worksheet.getColumn(i).width = MONTH_COL_WIDTH;
  }

  return workbook;
}

export async function writeSemifinishedRequirementWorkbookBuffer(
  items: SemifinishedRequirementItem[],
  months: string[],
  splitBySemifinished: boolean
): Promise<ExcelJS.Buffer> {
  const workbook = buildSemifinishedRequirementWorkbook(
    items,
    months,
    splitBySemifinished
  );
  return await workbook.xlsx.writeBuffer();
}
